import { useRef, useState } from 'react'
import { Palette } from 'lucide-react'

import { FloatingPanel } from './FloatingPanel'

// Color row used across the block editors. In addition to the standard
// hex picker + text input, exposes:
//   • a "Transparent" preset (sets the value to "transparent")
//   • theme-token presets that resolve to the page's current palette
//     ("var(--pg-accent)", "var(--pg-text)", etc.)
// Both options are surfaced through a small popover behind the palette
// icon so the row keeps its compact width.

interface Props {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}

interface ThemeOption {
  name: string
  value: string
  swatchVar: string              // CSS variable used to render a live swatch
}

const THEME_OPTIONS: ThemeOption[] = [
  { name: 'Primary',        value: 'var(--pg-primary)',        swatchVar: 'var(--pg-primary)' },
  { name: 'Accent',         value: 'var(--pg-accent)',         swatchVar: 'var(--pg-accent)' },
  { name: 'Text',           value: 'var(--pg-text)',           swatchVar: 'var(--pg-text)' },
  { name: 'Text secondary', value: 'var(--pg-text-secondary)', swatchVar: 'var(--pg-text-secondary)' },
  { name: 'Background',     value: 'var(--pg-bg)',             swatchVar: 'var(--pg-bg)' },
  { name: 'Surface',        value: 'var(--pg-surface)',        swatchVar: 'var(--pg-surface)' },
  { name: 'Border',         value: 'var(--pg-border)',         swatchVar: 'var(--pg-border)' },
  { name: 'Error',          value: 'var(--pg-error)',          swatchVar: 'var(--pg-error)' },
]

export default function ColorValueRow({ value, onChange, placeholder }: Props) {
  const validHex = /^#[0-9a-fA-F]{3,8}$/.test(value)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement | null>(null)

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', position: 'relative', flex: 1 }}>
      <input
        type="color"
        value={validHex && value.length === 7 ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        title="Pick a color"
        style={{
          width: 36, height: 28, padding: 0,
          border: '1px solid var(--color-border)', borderRadius: 6,
          background: 'transparent', cursor: 'pointer', flexShrink: 0,
        }}
      />
      <input
        type="text"
        value={value}
        placeholder={placeholder ?? '#hex / var(--…) / transparent'}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          padding: '6px 10px', fontSize: '0.9125rem',
          background: 'var(--color-bg)', border: '1px solid var(--color-border)',
          borderRadius: 6, color: 'var(--color-text)', outline: 'none',
          fontFamily: 'monospace',
        }}
      />
      <button
        ref={anchorRef}
        type="button"
        title="Transparent / theme defaults"
        aria-label="Color presets"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
        style={{
          width: 28, height: 28,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: open ? 'var(--color-accent-weak)' : 'var(--color-bg)',
          color: open ? 'var(--color-accent)' : 'var(--color-text-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 6, cursor: 'pointer', flexShrink: 0,
        }}
      >
        <Palette size={13} />
      </button>

      {open && (
        <FloatingPanel anchorRef={anchorRef} align="right" width={220} onClose={() => setOpen(false)}>
        <div
          style={{
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 8, padding: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          {/* "Use theme default" — clears the per-field override so the
              value falls back to whatever the page theme provides for this
              slot (e.g. heading inherits palette.text). Editors that bind
              empty string to undefined treat this as "unset". */}
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false) }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', borderRadius: 6,
              background: value === '' ? 'var(--color-accent-weak)' : 'transparent',
              color: 'var(--color-text)', border: '1px solid var(--color-border)',
              cursor: 'pointer', fontSize: '0.8825rem', textAlign: 'left',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 18, height: 18, borderRadius: 4,
                border: '1px dashed var(--color-border)',
                background: 'transparent',
                flexShrink: 0,
              }}
            />
            <span>Use theme default</span>
          </button>

          <button
            type="button"
            onClick={() => { onChange('transparent'); setOpen(false) }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', borderRadius: 6,
              background: value === 'transparent' ? 'var(--color-accent-weak)' : 'transparent',
              color: 'var(--color-text)', border: '1px solid var(--color-border)',
              cursor: 'pointer', fontSize: '0.8825rem', textAlign: 'left',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 18, height: 18, borderRadius: 4,
                border: '1px solid var(--color-border)',
                background: 'repeating-conic-gradient(#cbd5e1 0% 25%, #f8fafc 0% 50%) 50% / 8px 8px',
                flexShrink: 0,
              }}
            />
            <span>Transparent</span>
          </button>

          <div>
            <div style={{
              fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.04em', color: 'var(--color-text-secondary)',
              marginBottom: 6,
            }}>Theme defaults</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {THEME_OPTIONS.map(opt => {
                const active = value === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false) }}
                    title={opt.value}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 6px', borderRadius: 4,
                      background: active ? 'var(--color-accent-weak)' : 'transparent',
                      color: 'var(--color-text)',
                      border: '1px solid var(--color-border)',
                      cursor: 'pointer', fontSize: '0.8425rem', textAlign: 'left',
                      minWidth: 0,
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: '1px solid var(--color-border)',
                        background: opt.swatchVar,
                      }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.name}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        </FloatingPanel>
      )}
    </div>
  )
}
